import React, { useContext, useMemo, useState, useEffect } from 'react';
import { Box, Typography, FormControl, InputLabel, Select, MenuItem, Paper, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material';
import { useQuery } from 'react-query';
import { ThemeContext } from 'styled-components';
import Authorized from '../../../layouts/Authorized';
import { AuthContext } from '../../auth/providers/AuthProvider';
import { CsrfContext } from '../../../providers/CsrfProvider';
import { useGroup } from '../../../providers/GroupProvider';
import { getDashboardDetails, listUsersTemplates } from '../../../services/template/Templates';
import DashboardActivityChart from '../components/DashboardActivityChart';
import FormatNumber from '../../../utils/formatters/FormatNumber';

const BILL_ALL = 'all';

// Match participants pane / table styling (FormParticipants, ViewDefaultBill)
const tableContainerSx = (theme) => ({
    mb: 0,
    pb: 0,
    mt: 0,
    pt: 0,
    mr: 0,
    ml: 0,
    width: '100%',
    borderRadius: 2,
    backgroundColor: theme.colors.tableBackground || 'white',
    border: 'none !important',
    borderBottom: 'none !important',
    borderTop: 'none !important',
    boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
    outline: 'none !important',
    overflow: 'hidden',
    '&::before': { display: 'none !important' },
    '&::after': { display: 'none !important' },
    '&.MuiPaper-root': {
        border: 'none !important',
        borderBottom: 'none !important',
        borderTop: 'none !important',
        boxShadow: theme.shadows?.table || '0 4px 12px rgba(0, 0, 0, 0.15)',
        marginBottom: '0 !important',
        paddingBottom: '0 !important',
        outline: 'none !important',
        borderRadius: 2,
        '&::before': { display: 'none !important' },
        '&::after': { display: 'none !important' },
    },
    '& .MuiTable-root': {
        border: 'none !important',
        borderBottom: 'none !important',
        borderTop: 'none !important',
        marginBottom: '0 !important',
        '& .MuiTableBody-root': {
            border: 'none !important',
            borderBottom: 'none !important',
            '& .MuiTableRow-root:last-child .MuiTableCell-root': {
                borderBottom: 'none !important',
            },
        },
    },
});

const sectionWrapperSx = {
    width: '100%',
    border: 'none',
    borderBottom: 'none',
    mb: '40px',
    '& .MuiTableContainer-root': {
        borderBottom: 'none !important',
        '&::after': { display: 'none !important' },
    },
};

const ViewDashboard = () => {
    const theme = useContext(ThemeContext);
    const { csrfToken } = useContext(CsrfContext);
    const { authClaims } = useContext(AuthContext);
    const { selectedGroupId, loading: groupsLoading, hydratedBills, billsGroupId } = useGroup();
    const currentUserSub = authClaims?.user_sub ?? authClaims?.sub;
    const [selectedBillId, setSelectedBillId] = useState(BILL_ALL);

    // Never send "all" when we have a last-viewed group from localStorage; only use group_id from selectedGroupId.
    const dashboardApiGroupId =
        selectedGroupId === 'all' || selectedGroupId == null || selectedGroupId === undefined
            ? 'all'
            : selectedGroupId;

    // Reuse bills from GET /api/groups?bills=true when selected group matches; otherwise use shared templates cache (Sidebar/Footer).
    const selectedMatchesHydrated =
        (selectedGroupId == null && (billsGroupId == null || billsGroupId === '')) ||
        (selectedGroupId && billsGroupId && String(selectedGroupId) === String(billsGroupId));

    // Only run when we have a resolved selectedGroupId (from localStorage or after loadGroups). Never run with group_id=all when selectedGroupId is null — wait for loadGroups to set it.
    const hasSelectedGroup = selectedGroupId === 'all' || (selectedGroupId != null && selectedGroupId !== '');
    const dashboardQueryEnabled = !!csrfToken && !!authClaims && hasSelectedGroup;

    // Log so we can trace why we get group_id=all or delayed dashboard request.
    useEffect(() => {
        console.log('[ViewDashboard] dashboard query', {
            selectedGroupId,
            groupsLoading,
            dashboardApiGroupId,
            hasSelectedGroup,
            dashboardQueryEnabled,
            hasCsrf: !!csrfToken,
            hasAuth: !!authClaims,
        });
    }, [selectedGroupId, groupsLoading, dashboardApiGroupId, hasSelectedGroup, dashboardQueryEnabled, csrfToken, authClaims]);

    const { data, isLoading, isError, error } = useQuery(
        ['dashboard', selectedGroupId],
        () => getDashboardDetails(csrfToken, dashboardApiGroupId),
        { enabled: dashboardQueryEnabled }
    );

    // Only run templates query when we don't already have bills from GET /api/groups?bills=true (avoids GET /api/bills/templates on refresh).
    const { data: fetchedBills = [] } = useQuery(
        ['templates', selectedGroupId],
        () => listUsersTemplates(csrfToken, selectedGroupId ?? null),
        {
            enabled: !!csrfToken && !!authClaims && !groupsLoading && !selectedMatchesHydrated,
            refetchOnWindowFocus: false,
        }
    );
    const bills = selectedMatchesHydrated && Array.isArray(hydratedBills) ? hydratedBills : fetchedBills;

    const statements = data?.statements ?? [];
    const transactions = data?.transactions ?? [];
    const ownedStatementIds = data?.owned_statement_ids ?? [];

    const statementsFiltered = useMemo(() => {
        if (selectedBillId === BILL_ALL) return statements;
        return statements.filter((s) => String(s.bill_id ?? s.billId) === String(selectedBillId));
    }, [statements, selectedBillId]);

    const transactionsFiltered = useMemo(() => {
        if (selectedBillId === BILL_ALL) return transactions;
        return transactions.filter((t) => String(t.bill_id ?? t.billId) === String(selectedBillId));
    }, [transactions, selectedBillId]);

    const billOptions = useMemo(() => {
        const list = (bills || [])
            .filter((b) => b && b.id)
            .filter((b) => !b.archived_at || b.archived_at === '');
        return [{ id: BILL_ALL, name: 'All bills' }, ...list];
    }, [bills]);

    const currentBill = useMemo(() => {
        if (!selectedBillId || selectedBillId === BILL_ALL) return null;
        return (bills || []).find((b) => String(b.id) === String(selectedBillId)) ?? null;
    }, [bills, selectedBillId]);

    const isOwnerBill = useMemo(() => {
        if (!currentBill?.owner_sub || !currentUserSub) return false;
        return String(currentBill.owner_sub).toUpperCase() === String(currentUserSub).toUpperCase();
    }, [currentBill?.owner_sub, currentUserSub]);

    const myBalance = useMemo(() => {
        if (!currentBill?.participants || !currentUserSub) return null;
        const p = currentBill.participants.find(
            (x) => x.user_sub && String(x.user_sub).toUpperCase() === String(currentUserSub).toUpperCase()
        );
        return p?.balance != null ? Number(p.balance) : null;
    }, [currentBill?.participants, currentUserSub]);

    const owesRows = useMemo(() => {
        if (!isOwnerBill || !currentBill?.participants) return [];
        return currentBill.participants.filter((p) => {
            const bal = p?.balance;
            return bal != null && Number(bal) < 0;
        });
    }, [isOwnerBill, currentBill?.participants]);

    useEffect(() => {
        setSelectedBillId(BILL_ALL);
    }, [selectedGroupId]);

    if (isLoading) {
        return (
            <Authorized>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        position: 'fixed',
                        overflow: 'hidden',
                        backgroundColor: theme?.colors?.backgroundSecondary,
                    }}
                >
                    <Typography sx={{ color: theme?.colors?.text }}>Loading dashboard...</Typography>
                </Box>
            </Authorized>
        );
    }

    if (isError) {
        return (
            <Authorized>
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        height: '100%',
                        width: '100%',
                        position: 'fixed',
                        overflow: 'hidden',
                        backgroundColor: theme?.colors?.backgroundSecondary,
                    }}
                >
                    <Typography color="error">
                        Failed to load dashboard: {error?.message || 'Unknown error'}
                    </Typography>
                </Box>
            </Authorized>
        );
    }

    return (
        <Authorized>
            <Box
                sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'stretch',
                    height: '100%',
                    width: '100%',
                    position: 'fixed',
                    overflow: 'hidden',
                    backgroundColor: theme.colors.backgroundSecondary,
                }}
            >
                <Box
                    sx={{
                        width: { xs: '100%', sm: '75%', md: 'min(80%, calc(100% - 280px))' },
                        maxWidth: { xs: '100%', sm: '900px', md: 'min(900px, calc(100% - 280px))' },
                        left: { xs: '50%', sm: '54.5%', md: '53%' },
                        transform: 'translateX(-50%)',
                        top: { xs: '5vw', sm: '5vw' },
                        bottom: { xs: '5vw', sm: '0' },
                        height: { xs: 'calc(85vh - 64px)', sm: '85vh' },
                        margin: 'auto',
                        paddingLeft: { xs: 0, sm: '10px' },
                        paddingRight: { xs: 0, sm: '10px' },
                        position: 'fixed',
                        boxShadow: 20,
                        borderRadius: { xs: 0, sm: 4 },
                        display: 'flex',
                        flexDirection: 'column',
                        backgroundColor: theme.colors.backgroundPrimary,
                        boxSizing: 'border-box',
                        overflow: 'hidden',
                    }}
                >
                    {/* Dashboard header – matches SectionHeader (bills views) with bill selector */}
                    <Paper
                        sx={{
                            padding: 2,
                            margin: { xs: 0, sm: '0 -10px 0 -10px' },
                            borderRadius: { xs: 0, sm: '16px 16px 0 0' },
                            borderColor: theme.colors.headerBorder,
                            borderStyle: 'solid',
                            borderWidth: '2',
                            backgroundColor: theme.colors.primary,
                            backgroundImage: 'none',
                            color: theme.colors.secondary,
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            position: 'relative',
                            boxSizing: 'border-box',
                            width: { xs: '100%', sm: 'calc(100% + 20px)' },
                            overflow: 'visible',
                            pointerEvents: 'auto',
                        }}
                    >
                        <Typography variant="h6" sx={{ fontWeight: 'bold', textAlign: 'center' }}>
                            Dashboard
                        </Typography>
                        <Box
                            sx={{
                                position: 'absolute',
                                right: 8,
                                top: '50%',
                                transform: 'translateY(-50%)',
                                display: 'flex',
                                alignItems: 'center',
                                gap: 2,
                                zIndex: 10,
                                pointerEvents: 'auto',
                            }}
                        >
                            <FormControl
                                size="small"
                                sx={{
                                    minWidth: 200,
                                    '& .MuiOutlinedInput-root': {
                                        backgroundColor: 'rgba(255,255,255,0.12)',
                                        color: theme.colors.secondary,
                                        '& fieldset': {
                                            borderColor: theme.colors.secondary,
                                            opacity: 0.7,
                                        },
                                        '&:hover fieldset': { borderColor: theme.colors.secondary, opacity: 1 },
                                        '&.Mui-focused fieldset': { borderColor: theme.colors.secondary, opacity: 1 },
                                    },
                                    '& .MuiInputLabel-root': { color: theme.colors.secondary },
                                    '& .MuiSvgIcon-root': { color: theme.colors.secondary },
                                }}
                            >
                                <InputLabel id="dashboard-bill-label">Bill</InputLabel>
                                <Select
                                    labelId="dashboard-bill-label"
                                    id="dashboard-bill-select"
                                    value={selectedBillId}
                                    label="Bill"
                                    onChange={(e) => setSelectedBillId(e.target.value)}
                                    sx={{ color: theme.colors.secondary }}
                                    MenuProps={{
                                        PaperProps: {
                                            sx: {
                                                backgroundColor: theme.colors.backgroundPrimary || '#fff',
                                                color: theme.colors.text,
                                            },
                                        },
                                    }}
                                >
                                    {billOptions.map((opt) => (
                                        <MenuItem key={opt.id} value={opt.id} sx={{ color: theme.colors.text }}>
                                            {opt.name || opt.id}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Box>
                    </Paper>
                    {/* End dashboard header */}

                    {/* Activity pane – same structure as participants pane (scroll + section + table-style card) */}
                    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', marginRight: { xs: 0, sm: '-5px' }, boxSizing: 'border-box', overflow: 'hidden' }}>
                        <Box
                            sx={{
                                overflow: 'auto',
                                flex: 1,
                                minHeight: 0,
                                paddingRight: { xs: 0, sm: '5px' },
                                paddingTop: '40px',
                                boxSizing: 'border-box',
                                width: '100%',
                                '&::-webkit-scrollbar-thumb': {
                                    backgroundColor: `${theme.colors.brandSecondary || theme.colors.secondary} !important`,
                                },
                                scrollbarColor: `${theme.colors.brandSecondary || theme.colors.secondary} transparent`,
                            }}
                        >
                            <Box
                                sx={{
                                    paddingLeft: { xs: 0, sm: '10px' },
                                    paddingRight: { xs: 0, sm: '10px' },
                                    boxSizing: 'border-box',
                                    width: '100%',
                                    maxWidth: '100%',
                                    overflow: 'hidden',
                                }}
                            >
                                <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
                                    {/* Balance pane */}
                                    <Box sx={sectionWrapperSx}>
                                        <Box
                                            sx={{
                                                ...tableContainerSx(theme),
                                                display: 'flex',
                                                flexDirection: 'column',
                                                overflow: 'visible',
                                            }}
                                        >
                                            <Typography
                                                component="h2"
                                                sx={{
                                                    color: theme.colors.brandSecondary,
                                                    fontWeight: 700,
                                                    pl: 2,
                                                    pt: 2,
                                                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                                    textAlign: 'left',
                                                }}
                                            >
                                                Balance
                                            </Typography>
                                            <Box sx={{ px: 2, pb: 2, pt: 1 }}>
                                                {!currentBill ? (
                                                    <Typography variant="body2" sx={{ color: theme.colors.text, fontStyle: 'italic' }}>
                                                        Select a bill to see balance.
                                                    </Typography>
                                                ) : (
                                                    <>
                                                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: owesRows.length ? 2 : 0 }}>
                                                            {myBalance != null && (
                                                                <>
                                                                    {myBalance < 0 && (
                                                                        <Typography variant="body1" sx={{ color: theme.colors.text }}>
                                                                            You owe: <Typography component="span" sx={{ fontWeight: 600, color: theme.colors.error || '#f44336' }}>${FormatNumber(Math.abs(myBalance))}</Typography>
                                                                        </Typography>
                                                                    )}
                                                                    {myBalance > 0 && (
                                                                        <Typography variant="body1" sx={{ color: theme.colors.text }}>
                                                                            You are owed: <Typography component="span" sx={{ fontWeight: 600, color: theme.colors.success || '#4caf50' }}>${FormatNumber(myBalance)}</Typography>
                                                                        </Typography>
                                                                    )}
                                                                    {myBalance === 0 && (
                                                                        <Typography variant="body1" sx={{ color: theme.colors.text }}>
                                                                            Balance: $0
                                                                        </Typography>
                                                                    )}
                                                                </>
                                                            )}
                                                            {myBalance == null && (
                                                                <Typography variant="body2" sx={{ color: theme.colors.text, fontStyle: 'italic' }}>
                                                                    No balance data for this bill.
                                                                </Typography>
                                                            )}
                                                        </Box>
                                                        {isOwnerBill && owesRows.length > 0 && (
                                                            <>
                                                                <Typography variant="subtitle2" sx={{ color: theme.colors.text, fontWeight: 600, mb: 1 }}>
                                                                    Owes
                                                                </Typography>
                                                                <Table size="small" sx={{ border: 'none' }}>
                                                                    <TableHead>
                                                                        <TableRow>
                                                                            <TableCell sx={{ color: theme.colors.text, fontWeight: 600, pl: 0, border: 'none', py: 0.5 }}>User</TableCell>
                                                                            <TableCell align="right" sx={{ color: theme.colors.text, fontWeight: 600, border: 'none', py: 0.5 }}>Outstanding</TableCell>
                                                                        </TableRow>
                                                                    </TableHead>
                                                                    <TableBody>
                                                                        {owesRows.map((p) => (
                                                                            <TableRow key={p.id || p.user_sub || p.email}>
                                                                                <TableCell sx={{ color: theme.colors.text, pl: 0, border: 'none', py: 0.5 }}>{p.email || p.user_sub || '—'}</TableCell>
                                                                                <TableCell align="right" sx={{ color: theme.colors.error || '#f44336', fontWeight: 500, border: 'none', py: 0.5 }}>${FormatNumber(Math.abs(Number(p.balance)))}</TableCell>
                                                                            </TableRow>
                                                                        ))}
                                                                    </TableBody>
                                                                </Table>
                                                            </>
                                                        )}
                                                    </>
                                                )}
                                            </Box>
                                        </Box>
                                    </Box>

                                    {/* Activity pane */}
                                    <Box sx={sectionWrapperSx}>
                                        <Box
                                            sx={{
                                                ...tableContainerSx(theme),
                                                flex: 1,
                                                minHeight: 320,
                                                display: 'flex',
                                                flexDirection: 'column',
                                            }}
                                        >
                                            {/* Pane header – same style as Statements / Participants, left justified */}
                                            <Typography
                                                component="h2"
                                                sx={{
                                                    color: theme.colors.brandSecondary,
                                                    fontWeight: 700,
                                                    pl: 2,
                                                    pt: 2,
                                                    fontSize: { xs: '1.5rem', sm: '1.75rem' },
                                                    textAlign: 'left',
                                                }}
                                            >
                                                Activity
                                            </Typography>
                                            <Box sx={{ flex: 1, minHeight: 280 }}>
                                                <DashboardActivityChart
                                                    statements={statementsFiltered}
                                                    transactions={transactionsFiltered}
                                                    currentUserSub={currentUserSub}
                                                    ownedStatementIds={ownedStatementIds}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Authorized>
    );
};

export default ViewDashboard;
